import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import InventoryIcon from "@mui/icons-material/Inventory2Outlined";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import productsServices from "../../../services/productsService";

const statusMap = {
  pending: { label: "Pendiente", color: "default" },
  in_progress: { label: "En proceso", color: "info" },
  accepted: { label: "Aceptado", color: "success" },
  partially_accepted: { label: "Parcial", color: "warning" },
  prepared: { label: "Preparado", color: "info" },
  located: { label: "Ubicado", color: "success" },
  done: { label: "Despachado", color: "success" },
  closed: { label: "Cerrado", color: "default" },
  cancelled: { label: "Cancelado", color: "error" },
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  }) + " " + d.toLocaleTimeString("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function HistorialModal({ open, setOpen, productoSKU, productoNombre }) {
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(true);

  // Calcula cantidad de unidades que afectan al producto en un movimiento
  // Suma directo + combo (ambos son egresos reales del producto)
  const calcCantidad = (mov, campo) => {
    let total = 0;
    for (const detail of mov.movement_details || []) {
      const detailProductId = parseInt(detail.product_id || "0", 10);
      if (detailProductId === productoSKU) {
        total += detail[campo] || 0;
      } else if (detail.combo_component_quantity) {
        total += (detail[campo] || 0) * detail.combo_component_quantity;
      }
    }
    return total;
  };

  useEffect(() => {
    if (productoSKU && open) {
      setLoading(true);
      productsServices.getHistory(productoSKU).then((resp) => {
        const movimientosValidos = (resp.data.results || []).filter(
          (mov) => mov.status !== "cancelled"
        );

        const movimientosDelProducto = movimientosValidos.filter((mov) => {
          if (!mov.movement_details || mov.movement_details.length === 0) return false;
          return mov.movement_details.some((detail) => {
            const detailProductId = parseInt(detail.product_id || "0", 10);
            return detailProductId === productoSKU || !!detail.combo_component_quantity;
          });
        });

        const movimientosOrdenados = movimientosDelProducto.sort(
          (a, b) => new Date(a.date_created) - new Date(b.date_created)
        );

        const estadosEfectivosIn = ["accepted", "partially_accepted", "located", "closed"];
        const estadosEfectivosOut = ["done", "prepared"];

        const historialConStock = movimientosOrdenados.map((movimiento, index, array) => {
          let stockActual = 0;
          for (let i = 0; i <= index; i++) {
            const mov = array[i];
            const estadosValidos = mov.type === "in" ? estadosEfectivosIn : estadosEfectivosOut;
            if (!estadosValidos.includes(mov.status)) continue;

            let cantidad = 0;
            for (const detail of mov.movement_details || []) {
              const detailProductId = parseInt(detail.product_id || "0", 10);
              if (detailProductId === productoSKU) {
                cantidad += detail.effective_count || 0;
              } else if (detail.combo_component_quantity) {
                cantidad += (detail.effective_count || 0) * detail.combo_component_quantity;
              }
            }
            if (mov.type === "in") stockActual += cantidad;
            else if (mov.type === "out") stockActual -= cantidad;
          }
          return { ...movimiento, stockTotal: stockActual };
        });

        setHistorial(historialConStock);
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [open, productoSKU]);

  const handleClose = () => {
    setLoading(true);
    setHistorial([]);
    setOpen(false);
  };

  const CustomToolbar = () => (
    <GridToolbarContainer sx={{ px: 3, py: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <GridToolbarExport
        csvOptions={{ fileName: `historial_${productoSKU}` }}
        printOptions={{ disableToolbarButton: true }}
        sx={{ fontSize: "0.8rem" }}
      />
      <GridToolbarQuickFilter
        placeholder="Buscar por orden, estado..."
        sx={{ width: 320 }}
        debounceMs={300}
      />
    </GridToolbarContainer>
  );

  const columns = [
    {
      headerName: "Fecha",
      field: "date_created",
      flex: 1,
      minWidth: 150,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "#555" }}>
          {formatDate(row.date_created)}
        </Typography>
      ),
    },
    {
      headerName: "Orden",
      field: "id",
      flex: 0.6,
      minWidth: 80,
      headerAlign: "center",
      align: "center",
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: "#2563EB" }}>
          #{row.id}
        </Typography>
      ),
    },
    {
      headerName: "Tipo",
      field: "type",
      flex: 0.9,
      minWidth: 110,
      headerAlign: "center",
      align: "center",
      renderCell: ({ row }) => (
        <Chip
          icon={row.type === "in" ? <TrendingUpIcon sx={{ fontSize: 16 }} /> : <TrendingDownIcon sx={{ fontSize: 16 }} />}
          label={row.type === "in" ? "Ingreso" : "Egreso"}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: "0.75rem",
            backgroundColor: row.type === "in" ? "#e8f5e9" : "#fce4ec",
            color: row.type === "in" ? "#2e7d32" : "#c62828",
            "& .MuiChip-icon": { color: row.type === "in" ? "#2e7d32" : "#c62828" },
          }}
        />
      ),
    },
    {
      headerName: "Estado",
      field: "status",
      flex: 0.9,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      renderCell: ({ row }) => {
        const s = statusMap[row.status] || { label: row.status, color: "default" };
        return <Chip label={s.label} color={s.color} size="small" variant="outlined" sx={{ fontSize: "0.72rem", fontWeight: 500 }} />;
      },
    },
    {
      headerName: "Cantidad",
      field: "cantidad",
      flex: 0.8,
      minWidth: 100,
      headerAlign: "center",
      align: "center",
      renderCell: ({ row }) => {
        const qty = calcCantidad(row, "effective_count");
        if (!qty) return <Typography variant="body2" sx={{ color: "#bbb" }}>—</Typography>;
        const isIn = row.type === "in";
        return (
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.9rem", color: isIn ? "#2e7d32" : "#c62828" }}>
            {isIn ? "+" : "−"}{qty}
          </Typography>
        );
      },
    },
    {
      headerName: "Stock",
      field: "stockTotal",
      flex: 0.8,
      minWidth: 90,
      headerAlign: "center",
      align: "center",
      renderCell: ({ row }) => {
        const val = row.stockTotal ?? 0;
        const isNeg = val < 0;
        return (
          <Box sx={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            px: 1.5, py: 0.3, borderRadius: 1,
            backgroundColor: isNeg ? "#fce4ec" : "#e8f5e9",
            minWidth: 40, height: 28,
          }}>
            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.9rem", color: isNeg ? "#c62828" : "#2e7d32" }}>
              {val}
            </Typography>
          </Box>
        );
      },
    },
  ];

  // Resumen
  const totalIngresos = historial.reduce((acc, m) => acc + (m.type === "in" ? calcCantidad(m, "effective_count") : 0), 0);
  const totalEgresos = historial.reduce((acc, m) => acc + (m.type === "out" ? calcCantidad(m, "effective_count") : 0), 0);
  const stockFinal = historial.length > 0 ? historial[historial.length - 1].stockTotal : 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}>
      {/* Header */}
      <DialogTitle sx={{
        backgroundColor: "#fff",
        color: "#1a1a1a", py: 2, px: 3,
        borderBottom: "1px solid #e2e8f0",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 36, height: 36, borderRadius: "9px", bgcolor: "#E8F0FE", flexShrink: 0,
            }}
          >
            <InventoryIcon sx={{ fontSize: 20, color: "primary.main" }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.05rem", lineHeight: 1.2, color: "#1a1a1a" }}>
              Historial de Movimientos
            </Typography>
            {productoNombre && (
              <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.8rem" }}>
                {productoNombre}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: "#64748b", "&:hover": { color: "#1a1a1a", backgroundColor: "#f1f5f9" } }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Resumen */}
        {!loading && historial.length > 0 && (
          <Box sx={{ display: "flex", gap: 2, px: 3, py: 1.5, borderBottom: "1px solid #eee", bgcolor: "#fafafa" }}>
            <Tooltip title="Total unidades ingresadas">
              <Chip
                icon={<TrendingUpIcon sx={{ fontSize: 16 }} />}
                label={`+${totalIngresos} ingresadas`}
                size="small"
                sx={{ fontWeight: 600, bgcolor: "#e8f5e9", color: "#2e7d32", "& .MuiChip-icon": { color: "#2e7d32" } }}
              />
            </Tooltip>
            <Tooltip title="Total unidades egresadas">
              <Chip
                icon={<TrendingDownIcon sx={{ fontSize: 16 }} />}
                label={`−${totalEgresos} egresadas`}
                size="small"
                sx={{ fontWeight: 600, bgcolor: "#fce4ec", color: "#c62828", "& .MuiChip-icon": { color: "#c62828" } }}
              />
            </Tooltip>
            <Chip
              label={`Stock actual: ${stockFinal}`}
              size="small"
              sx={{
                fontWeight: 700, ml: "auto",
                bgcolor: stockFinal < 0 ? "#c62828" : "#2e7d32", color: "#fff",
              }}
            />
          </Box>
        )}

        {/* Tabla */}
        <Box sx={{ height: "60vh", width: "100%" }}>
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <DataGrid
              rows={historial}
              columns={columns}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
                sorting: { sortModel: [{ field: "date_created", sort: "desc" }] },
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              disableRowSelectionOnClick
              slots={{ toolbar: CustomToolbar }}
              getRowId={(row) => `${row.id}-${row.date_created}`}
              rowHeight={48}
              sx={{
                border: "none",
                "& .MuiDataGrid-columnHeader:first-of-type, & .MuiDataGrid-cell:first-of-type": {
                  pl: 3,
                },
                "& .MuiDataGrid-columnHeader:last-of-type, & .MuiDataGrid-cell:last-of-type": {
                  pr: 3,
                },
                "& .MuiDataGrid-columnHeaders": {
                  bgcolor: "#f5f5f5",
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  color: "#666",
                },
                "& .MuiDataGrid-columnHeaderTitleContainer": {
                  justifyContent: "inherit",
                },
                "& .MuiDataGrid-row:hover": { bgcolor: "#f8f9ff" },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #f0f0f0",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiDataGrid-footerContainer": { borderTop: "1px solid #eee" },
              }}
              localeText={{
                noRowsLabel: "Sin movimientos registrados",
                toolbarExport: "Exportar",
                toolbarExportCSV: "Exportar CSV",
                toolbarExportPrint: "Imprimir",
                MuiTablePagination: {
                  labelRowsPerPage: "Filas por página:",
                  labelDisplayedRows: ({ from, to, count }) => `${from}–${to} de ${count}`,
                },
              }}
            />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
